// jscs:disable requireCamelCaseOrUpperCaseIdentifiers
import Util from './Util';

/**
 * Represents an address and stores its parts as properties. Used address-parts are defined in the Google Maps API at
 * https://developers.google.com/maps/documentation/geocoding/intro#Types
 */
export default class Address {

    /**
     * Unused constructor.
     */
    constructor() {
    }

    /**
     * Generate a new address by parsing the given geocoder-result.
     * @param {Object} result geocoder result.
     * @returns {Address} the geocoder-result address
     */
    static fromGeoCoderResult(result) {
        let address = new Address();
        if (Util.isSet(result) && result.hasOwnProperty('address_components')) {
            // copy result geometry to address
            address.geometry = result.geometry;
            // copy all long-texts of the address-components from the result into adress-properties
            for (const addressComponentId in result.address_components) {
                if (result.address_components.hasOwnProperty(addressComponentId)) {
                    const addressComponent = result.address_components[addressComponentId];
                    let addressType = addressComponent.types[0];
                    address[addressType] = addressComponent.long_name;
                }
            }
        }
        return address;
    }

    /**
     * Picks the most accurate result as the best result from the given array and generates an address out of it.
     * @param {Array} results geocoder-result array.
     * @returns {Address} most accurate geo address
     */
    static fromGeoCoderResults(results = []) {
        let bestResult = null;
        for (const resultId in results) {
            if (results.hasOwnProperty(resultId)) {
                const result = results[resultId];
                if (result.hasOwnProperty('geometry') && result.geometry.hasOwnProperty('location_type')) {
                    // find the most accurate (best) result by looking at the location-type
                    const locationType = result.geometry.location_type;
                    const usedLocationType = (Util.isSet(bestResult) ? bestResult.geometry.location_type : null);
                    switch (locationType) {
                        case google.maps.GeocoderLocationType.ROOFTOP:
                            bestResult = result;
                            break;
                        case google.maps.GeocoderLocationType.RANGE_INTERPOLATED:
                            if (!Util.isSet(bestResult) ||
                                    usedLocationType === google.maps.GeocoderLocationType.GEOMETRIC_CENTER ||
                                    usedLocationType === google.maps.GeocoderLocationType.APPROXIMATE) {
                                bestResult = result;
                            }
                            break;
                        case google.maps.GeocoderLocationType.GEOMETRIC_CENTER:
                            if (!Util.isSet(bestResult) ||
                                    usedLocationType === google.maps.GeocoderLocationType.APPROXIMATE) {
                                bestResult = result;
                            }
                            break;
                        default:
                            if (!Util.isSet(bestResult)) {
                                bestResult = result;
                            }
                            break;
                    }
                }
            }
        }
        // no good result found: take the first one
        if (!Util.isSet(bestResult) && results.length > 0) {
            bestResult = results[0];
        }
        // build address from best matching result
        return Address.fromGeoCoderResult(bestResult);
    }

    /**
     * Get a string-representation of the current address by connecting the most important address-parts. Can also build
     * a HTML representation if the flag is given.
     * @param {boolean} asHtml flag defining if the resulting string should be an HTML or not; defaults to false.
     * @returns {string} concatenated address-parts as string.
     */
    toString(asHtml = false) {
        let address = '';
        if (this.hasOwnProperty('route') && !Util.isEmpty(this.route)) {
            address += (asHtml ? Util.escapeHtml(this.route) : this.route);
        }
        if (this.hasOwnProperty('street_number') && !Util.isEmpty(this.street_number)) {
            address += (address ? ' ' : '') + (asHtml ? Util.escapeHtml(this.street_number) : this.street_number);
        }
        if (this.hasOwnProperty('postal_code') && !Util.isEmpty(this.postal_code)) {
            address += (address ? (asHtml ? '<br/>' : ', ') : '') +
                (asHtml ? Util.escapeHtml(this.postal_code) : this.postal_code);
        }
        if (this.hasOwnProperty('locality') && !Util.isEmpty(this.locality)) {
            address += (address ? (asHtml ? ' ' : ', ') : '') +
                (asHtml ? Util.escapeHtml(this.locality) : this.locality);
        }
        if (this.hasOwnProperty('country') && !Util.isEmpty(this.country)) {
            address += (address ? (asHtml ? '<br/>' : ', ') : '') +
                (asHtml ? Util.escapeHtml(this.country) : this.country);
        }
        return address;
    }

    /**
     * Checks whether the current address equals to the given address.
     * @param {Address} address other address instance to check on equality.
     * @returns {boolean} true if the addresses are equal by comparing their string-representation.
     */
    equals(address) {
        return (Util.isSet(address) && typeof address.toString === 'function' &&
            address.toString() === this.toString());
    }
}